import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, Database, Download, FileSpreadsheet,
  Info, Loader2, RefreshCw, Shield, Table2, Wallet,
} from 'lucide-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

// ---------------------------------------------------------------------------
// Gürgençler — Günlük Ham Veri (Ali Bey talebi)
//
// Sayfa, backend'in tuttuğu anlık görüntüyü okur; veri `scripts/
// gurgencler_alibey_export.js` tarafından üretilir (~7-8 dk). Buradaki tablolar
// Excel'in aynısıdır — Excel indirilip olduğu gibi gönderilebilir.
// ---------------------------------------------------------------------------

const API = 'https://api.glowytics.com/api/gurgencler-raw';

const nfInt = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
const nfTRY = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 });
// Marka terimlerinde CPC 1 TL'nin altında — kuruşsuz biçim her satırı "₺1"
// gösteriyordu, CPC sütunları bu yüzden ayrı biçimlendiriliyor.
const nfTRY2 = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = (v) => nfInt.format(Number(v) || 0);
const tl = (v) => nfTRY.format(Number(v) || 0);
const tl2 = (v) => nfTRY2.format(Number(v) || 0);
const pct = (v) => (v === null || v === undefined ? '—' : `%${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const gun = (d) => String(d || '').slice(5).replace('-', '.');
const ay = (d) => String(d || '').slice(0, 7);

const TABS = [
  { key: 'butce', label: '1 · Bütçe Değişimi', icon: Wallet },
  { key: 'google', label: '2 · Google Günlük', icon: Table2 },
  { key: 'meta', label: '3 · Meta Günlük', icon: Table2 },
  { key: 'ga4', label: '4 · Trafik Kaynağı', icon: Database },
  { key: 'marka', label: '5 · Marka Savunması', icon: Shield },
];

function Card({ title, icon: Icon, hint, children, tone }) {
  const ring = tone === 'warn' ? 'border-amber-200' : tone === 'bad' ? 'border-rose-200' : 'border-gray-200';
  return (
    <section className={`rounded-2xl border ${ring} bg-white shadow-sm overflow-hidden`}>
      {title && (
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          {hint && <span className="ml-auto text-[11px] text-gray-400">{hint}</span>}
        </div>
      )}
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}

function Table({ columns, rows, empty = 'Kayıt yok.' }) {
  if (!rows?.length) return <div className="px-5 py-8 text-sm text-gray-400">{empty}</div>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 bg-white sticky top-0">
          {columns.map((c) => (
            <th key={c.key} className={`px-4 py-2.5 font-medium ${c.right ? 'text-right' : ''}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-gray-50/70">
            {columns.map((c) => (
              <td key={c.key} className={`px-4 py-2 whitespace-nowrap ${c.right ? 'text-right tabular-nums' : ''} ${c.cls ? c.cls(r) : 'text-gray-700'}`}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Stat({ label, value, sub, tone }) {
  const color = tone === 'down' ? 'text-rose-600' : tone === 'up' ? 'text-emerald-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${color}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

/** Günlük satırları aya toplar. */
function aylik(rows, alanlar, donem) {
  const m = new Map();
  (rows || []).filter((r) => !donem || r.donem === donem).forEach((r) => {
    const k = ay(r.tarih);
    const e = m.get(k) || { ay: k };
    alanlar.forEach((a) => { e[a] = (e[a] || 0) + (Number(r[a]) || 0); });
    m.set(k, e);
  });
  return Array.from(m.values()).sort((a, b) => (a.ay < b.ay ? -1 : 1));
}

export default function GurgenclerRawData() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('butce');
  // Dönem etiketi artık tarih aralığının kendisi ("01.04.2026–31.07.2026"),
  // sabit bir varsayılan tutulamaz — veri gelince ilk dönem seçilir.
  const [donem, setDonem] = useState(null);
  const [refresh, setRefresh] = useState({ running: false, satirlar: [] });

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch(`${API}/data`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Üretim sürerken durumu yokla; bitince veriyi tazele.
  useEffect(() => {
    if (!refresh.running) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await fetch(`${API}/status`, { credentials: 'include' });
        const s = await res.json();
        setRefresh({ running: !!s.durum?.running, satirlar: s.sonSatirlar || [] });
        if (!s.durum?.running) load();
      } catch (_) { /* geçici hata — bir sonraki yoklamada düzelir */ }
    }, 5000);
    return () => clearInterval(t);
  }, [refresh.running, load]);

  const startRefresh = async () => {
    setRefresh({ running: true, satirlar: ['üretim başlatılıyor…'] });
    try {
      const res = await fetch(`${API}/refresh`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    } catch (e) { setErr(e.message); setRefresh({ running: false, satirlar: [] }); }
  };

  const donemler = useMemo(() => {
    const s = new Set();
    (data?.googleGunluk || []).forEach((r) => s.add(r.donem));
    return Array.from(s);
  }, [data]);

  // Seçili dönem veride yoksa (etiket değişti / tek döneme indi) ilkine dön.
  useEffect(() => {
    if (donemler.length && !donemler.includes(donem)) setDonem(donemler[0]);
  }, [donemler, donem]);

  const gAylik = useMemo(() => aylik(data?.googleGunluk, ['harcama', 'gosterim', 'tiklama', 'donusum_native', 'ciro_native'], donem), [data, donem]);
  const mAylik = useMemo(() => aylik(data?.metaGunluk, ['harcama', 'gosterim', 'tiklama', 'satis_native', 'ciro_native'], donem), [data, donem]);
  // Dört kova: Google/Meta Paid · Cimri/Akakçe · Organik · Diğer. Fiyat
  // karşılaştırma Paid'den ÇIKTI (11 Ağu 2026 müşteri talebi) — burada da ayrı
  // toplanmazsa hiçbir kovaya girmez, oturumlar sessizce eksilir.
  const kovaAylik = useMemo(() => aylik(data?.ga4Kova, ['organik_oturum', 'paid_oturum', 'fiyatkars_oturum', 'diger_oturum', 'organik_ciro', 'paid_ciro', 'fiyatkars_ciro', 'diger_ciro'], donem), [data, donem]);

  const harcamaSerisi = useMemo(() => {
    const m = new Map();
    gAylik.forEach((r) => m.set(r.ay, { ay: r.ay, google: Math.round(r.harcama) }));
    mAylik.forEach((r) => m.set(r.ay, { ...(m.get(r.ay) || { ay: r.ay }), meta: Math.round(r.harcama) }));
    return Array.from(m.values()).sort((a, b) => (a.ay < b.ay ? -1 : 1));
  }, [gAylik, mAylik]);

  const kovaSerisi = useMemo(() => (data?.ga4Kova || []).filter((r) => r.donem === donem)
    .map((r) => ({ tarih: r.tarih, Organik: r.organik_oturum, 'Google/Meta Paid': r.paid_oturum, 'Cimri/Akakçe': r.fiyatkars_oturum, 'Diğer': r.diger_oturum })), [data, donem]);

  const sonGunler = (rows) => (rows || []).filter((r) => r.donem === donem).slice(-45).reverse();

  if (loading && !data) {
    return <div className="flex items-center justify-center h-[60vh] text-gray-400 gap-2">
      <Loader2 className="w-5 h-5 animate-spin" /> Veri yükleniyor…
    </div>;
  }

  if (err && !data) {
    return (
      <div className="max-w-2xl mx-auto mt-16">
        <Card tone="bad">
          <div className="p-6">
            <div className="flex items-center gap-2 text-rose-700 font-semibold"><AlertTriangle className="w-4 h-4" /> Veri yok</div>
            <p className="mt-2 text-sm text-gray-600">{err}</p>
            <button onClick={startRefresh} disabled={refresh.running}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50">
              {refresh.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Veriyi üret (~8 dk)
            </button>
            {refresh.running && <pre className="mt-3 text-[11px] text-gray-500 whitespace-pre-wrap">{refresh.satirlar.join('\n')}</pre>}
          </div>
        </Card>
      </div>
    );
  }

  const u = data?.uretim || {};
  const metaDusukKapsam = (data?.metaGunluk || []).filter((r) => r.db_kapsam_pct !== null && r.db_kapsam_pct < 90).length;

  return (
    <div className="max-w-[1500px] mx-auto px-4 py-6 space-y-5">
      {/* Başlık */}
      <div className="rounded-2xl bg-gradient-to-r from-slate-900 to-slate-700 text-white px-6 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold">Gürgençler · Günlük Ham Veri</h1>
            <p className="text-sm text-white/70 mt-0.5">
              {u.cari?.start} → {u.cari?.end}
              {u.gecenYil && <> · geçen yıl {u.gecenYil.start} → {u.gecenYil.end}</>}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <a href={`${API}/zip`} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium hover:bg-emerald-400">
              <Download className="w-4 h-4" /> Tüm Excel'ler (ZIP)
            </a>
            <a href={`${API}/excel`} className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20">
              <FileSpreadsheet className="w-4 h-4" /> Tek dosya
            </a>
            <button onClick={startRefresh} disabled={refresh.running}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/20 disabled:opacity-50">
              {refresh.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {refresh.running ? 'Üretiliyor…' : 'Yeniden üret'}
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-white/60">
          <span>Üretim: {u.olusturuldu ? new Date(u.olusturuldu).toLocaleString('tr-TR') : '—'}</span>
          <span>Excel: {u.dosya}</span>
          <span>{n0(u.satirlar?.ga4Ham)} GA4 ham satır · {n0(u.satirlar?.gosterimPayi)} gösterim payı satırı</span>
        </div>
        {refresh.running && (
          <pre className="mt-3 text-[11px] text-white/70 whitespace-pre-wrap bg-black/20 rounded-lg p-3">{refresh.satirlar.join('\n')}</pre>
        )}
      </div>

      {/* Veri güvenilirliği uyarıları — sayfanın en kritik parçası */}
      <div className="grid md:grid-cols-3 gap-3">
        <Card tone="bad">
          <div className="p-4">
            <div className="flex items-center gap-2 text-rose-700 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4" /> Meta geçmişi platform DB'sinde eksik
            </div>
            <p className="mt-1.5 text-[13px] text-gray-600 leading-relaxed">
              {metaDusukKapsam} günde DB kapsamı %90'ın altında. Sayfadaki ve Excel'deki Meta rakamları
              <strong> Graph API'den</strong> alındı; DB değeri karşılaştırma sütununda duruyor.
            </p>
          </div>
        </Card>
        <Card tone="warn">
          <div className="p-4">
            <div className="flex items-center gap-2 text-amber-700 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4" /> Auction Insights rakip verisi yok
            </div>
            <p className="mt-1.5 text-[13px] text-gray-600 leading-relaxed">
              Google Ads API rakip bazlı auction insights vermiyor. Kendi gösterim payımız ve kayıp paylar
              tam; rakip örtüşme/dıştan sıralama arayüz export'uyla eklenecek.
            </p>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="flex items-center gap-2 text-gray-700 text-sm font-semibold">
              <Info className="w-4 h-4" /> Atıf kuralı
            </div>
            <p className="mt-1.5 text-[13px] text-gray-600 leading-relaxed">
              Transaction ve ciro tek kaynaktan: <strong>GA4</strong> (sayfa 4). Google/Meta sayfalarındaki
              "native" sütunlar platformun kendi atıfıdır — <strong>toplanmaz</strong>, çift sayım yaratır.
            </p>
          </div>
        </Card>
      </div>

      {/* Dönem seçici + sekmeler */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
          {donemler.map((d) => (
            <button key={d} onClick={() => setDonem(d)}
              className={`px-3 py-1.5 text-sm rounded-md ${donem === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {d}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${tab === t.key ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------------------------------------------------------- 1 */}
      {tab === 'butce' && (
        <div className="space-y-4">
          <Card title="Aylık medya harcaması" icon={Wallet} hint="Google = gerçek bütçe alanı · Meta = Graph API harcaması">
            <div className="p-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={harcamaSerisi}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="ay" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                  <Tooltip formatter={(v) => tl(v)} />
                  <Legend />
                  <Bar dataKey="google" name="Google" fill="#4285F4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="meta" name="Meta" fill="#0866FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Bütçe değişim kayıtları" icon={Wallet} hint={`${(data?.butceDegisim || []).filter((r) => r.donem === donem).length} kayıt`}>
            <div className="max-h-[560px] overflow-y-auto">
              <Table
                columns={[
                  { key: 'tarih', label: 'Tarih' },
                  { key: 'kanal', label: 'Kanal', cls: (r) => (r.kanal === 'Google Ads' ? 'text-blue-700' : 'text-indigo-700') },
                  { key: 'kampanya', label: 'Kampanya' },
                  { key: 'onceki_gunluk_butce', label: 'Önceki', right: true, render: (r) => (r.onceki_gunluk_butce == null ? '—' : tl(r.onceki_gunluk_butce)) },
                  { key: 'yeni_gunluk_butce', label: 'Yeni', right: true, render: (r) => (r.yeni_gunluk_butce == null ? '—' : tl(r.yeni_gunluk_butce)) },
                  {
                    key: 'degisim_pct',
                    label: 'Değişim',
                    right: true,
                    render: (r) => (r.degisim_pct == null ? '—' : (
                      <span className={`inline-flex items-center gap-1 ${r.degisim_pct < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {r.degisim_pct < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                        {pct(Math.abs(r.degisim_pct))}
                      </span>
                    )),
                  },
                  { key: 'kaynak', label: 'Kaynak', render: (r) => <span className="text-[11px] text-gray-400">{r.kaynak}</span> },
                ]}
                rows={(data?.butceDegisim || []).filter((r) => r.donem === donem)}
              />
            </div>
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- 2 */}
      {tab === 'google' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam harcama" value={tl(gAylik.reduce((a, b) => a + b.harcama, 0))} />
            <Stat label="Toplam tıklama" value={n0(gAylik.reduce((a, b) => a + b.tiklama, 0))} />
            <Stat label="Dönüşüm (Google atıf)" value={n0(gAylik.reduce((a, b) => a + b.donusum_native, 0))} />
            <Stat label="Ciro (Google atıf)" value={tl(gAylik.reduce((a, b) => a + b.ciro_native, 0))} />
          </div>
          <Card title="Günlük Google metrikleri" icon={Table2} hint="son 45 gün · tamamı Excel'de">
            <div className="max-h-[600px] overflow-y-auto">
              <Table
                columns={[
                  { key: 'tarih', label: 'Tarih' },
                  { key: 'harcama', label: 'Harcama', right: true, render: (r) => tl(r.harcama) },
                  { key: 'gunluk_butce_toplami', label: 'Günlük bütçe', right: true, render: (r) => tl(r.gunluk_butce_toplami) },
                  { key: 'gosterim', label: 'Gösterim', right: true, render: (r) => n0(r.gosterim) },
                  { key: 'tiklama', label: 'Tıklama', right: true, render: (r) => n0(r.tiklama) },
                  { key: 'ctr_pct', label: 'CTR', right: true, render: (r) => pct(r.ctr_pct) },
                  { key: 'cpc', label: 'CPC', right: true, render: (r) => tl2(r.cpc) },
                  { key: 'donusum_native', label: 'Dönüşüm', right: true, render: (r) => n0(r.donusum_native) },
                  { key: 'ciro_native', label: 'Ciro', right: true, render: (r) => tl(r.ciro_native) },
                  { key: 'aktif_kampanya', label: 'Aktif kamp.', right: true },
                ]}
                rows={sonGunler(data?.googleGunluk)}
              />
            </div>
          </Card>
          <Card title="Kampanya türü kırılımı" icon={Table2} hint="aylık toplam">
            <Table
              columns={[
                { key: 'kampanya_tipi', label: 'Kampanya türü' },
                { key: 'harcama', label: 'Harcama', right: true, render: (r) => tl(r.harcama) },
                { key: 'gosterim', label: 'Gösterim', right: true, render: (r) => n0(r.gosterim) },
                { key: 'tiklama', label: 'Tıklama', right: true, render: (r) => n0(r.tiklama) },
                { key: 'ciro_native', label: 'Ciro (Google atıf)', right: true, render: (r) => tl(r.ciro_native) },
              ]}
              rows={Object.values((data?.googleKampanyaTipi || []).filter((r) => r.donem === donem).reduce((acc, r) => {
                const e = acc[r.kampanya_tipi] || { kampanya_tipi: r.kampanya_tipi, harcama: 0, gosterim: 0, tiklama: 0, ciro_native: 0 };
                e.harcama += r.harcama; e.gosterim += r.gosterim; e.tiklama += r.tiklama; e.ciro_native += r.ciro_native;
                acc[r.kampanya_tipi] = e; return acc;
              }, {})).sort((a, b) => b.harcama - a.harcama)}
            />
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- 3 */}
      {tab === 'meta' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam harcama" value={tl(mAylik.reduce((a, b) => a + b.harcama, 0))} />
            <Stat label="Toplam gösterim" value={n0(mAylik.reduce((a, b) => a + b.gosterim, 0))} />
            <Stat label="Satış (Meta atıf)" value={n0(mAylik.reduce((a, b) => a + b.satis_native, 0))} />
            <Stat label="Ciro (Meta atıf)" value={tl(mAylik.reduce((a, b) => a + b.ciro_native, 0))} />
          </div>
          <Card title="Günlük Meta metrikleri" icon={Table2} hint="kaynak: Graph API · son 45 gün">
            <div className="max-h-[600px] overflow-y-auto">
              <Table
                columns={[
                  { key: 'tarih', label: 'Tarih' },
                  { key: 'harcama', label: 'Harcama', right: true, render: (r) => tl(r.harcama) },
                  { key: 'gosterim', label: 'Gösterim', right: true, render: (r) => n0(r.gosterim) },
                  { key: 'erisim', label: 'Erişim', right: true, render: (r) => (r.erisim == null ? '—' : n0(r.erisim)) },
                  { key: 'frekans', label: 'Frekans', right: true, render: (r) => (r.frekans == null ? '—' : r.frekans) },
                  { key: 'tiklama', label: 'Tıklama', right: true, render: (r) => n0(r.tiklama) },
                  { key: 'satis_native', label: 'Satış', right: true, render: (r) => n0(r.satis_native) },
                  { key: 'ciro_native', label: 'Ciro', right: true, render: (r) => tl(r.ciro_native) },
                  {
                    key: 'db_kapsam_pct',
                    label: 'DB kapsamı',
                    right: true,
                    render: (r) => (r.db_kapsam_pct == null ? '—' : (
                      <span className={r.db_kapsam_pct < 90 ? 'text-rose-600 font-medium' : 'text-gray-400'}>{pct(r.db_kapsam_pct)}</span>
                    )),
                  },
                ]}
                rows={sonGunler(data?.metaGunluk)}
              />
            </div>
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- 4 */}
      {tab === 'ga4' && (
        <div className="space-y-4">
          <Card title="Günlük oturum — Google/Meta Paid / Cimri-Akakçe / Organik / Diğer" icon={Database} hint="GA4 source-medium tabanlı">
            <div className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={kovaSerisi}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="tarih" tick={{ fontSize: 10 }} tickFormatter={gun} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                  <Tooltip formatter={(v) => n0(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="Google/Meta Paid" stackId="1" stroke="#f59e0b" fill="#fde68a" />
                  <Area type="monotone" dataKey="Cimri/Akakçe" stackId="1" stroke="#e11d48" fill="#fecdd3" />
                  <Area type="monotone" dataKey="Organik" stackId="1" stroke="#10b981" fill="#a7f3d0" />
                  <Area type="monotone" dataKey="Diğer" stackId="1" stroke="#6366f1" fill="#c7d2fe" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Aylık kova toplamları" icon={Database}>
            <Table
              columns={[
                { key: 'ay', label: 'Ay' },
                { key: 'paid_oturum', label: 'Google/Meta oturum', right: true, render: (r) => n0(r.paid_oturum) },
                { key: 'fiyatkars_oturum', label: 'Cimri/Akakçe oturum', right: true, render: (r) => n0(r.fiyatkars_oturum) },
                { key: 'organik_oturum', label: 'Organik oturum', right: true, render: (r) => n0(r.organik_oturum) },
                { key: 'diger_oturum', label: 'Diğer oturum', right: true, render: (r) => n0(r.diger_oturum) },
                { key: 'paid_ciro', label: 'Google/Meta ciro', right: true, render: (r) => tl(r.paid_ciro) },
                { key: 'fiyatkars_ciro', label: 'Cimri/Akakçe ciro', right: true, render: (r) => tl(r.fiyatkars_ciro) },
                { key: 'organik_ciro', label: 'Organik ciro', right: true, render: (r) => tl(r.organik_ciro) },
                { key: 'diger_ciro', label: 'Diğer ciro', right: true, render: (r) => tl(r.diger_ciro) },
              ]}
              rows={kovaAylik}
            />
          </Card>
          <Card title="En çok oturum getiren kaynak/medium" icon={Database} hint="dönem toplamı · ilk 60 · tamamı Excel'de">
            <div className="max-h-[520px] overflow-y-auto">
              <Table
                columns={[
                  { key: 'kaynak', label: 'Kaynak' },
                  { key: 'medium', label: 'Medium' },
                  { key: 'alt_kova', label: 'Alt kova' },
                  { key: 'oturum', label: 'Oturum', right: true, render: (r) => n0(r.oturum) },
                  { key: 'transaction', label: 'Transaction', right: true, render: (r) => n0(r.transaction) },
                  { key: 'ciro', label: 'Ciro', right: true, render: (r) => tl(r.ciro) },
                ]}
                rows={(data?.ga4Kaynaklar || []).filter((r) => r.donem === donem)}
              />
            </div>
          </Card>
        </div>
      )}

      {/* ---------------------------------------------------------------- 5 */}
      {tab === 'marka' && (
        <div className="space-y-4">
          <Card title="Gösterim payı ve kayıp paylar" icon={Shield} hint="kampanya ortalaması · marka kampanyası ayrı seri">
            <div className="p-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={(data?.gosterimPayiGunluk || []).filter((r) => r.donem === donem)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="tarih" tick={{ fontSize: 10 }} tickFormatter={gun} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `%${v}`} />
                  <Tooltip formatter={(v) => pct(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="gosterim_payi_pct" name="Gösterim payı" stroke="#10b981" dot={false} />
                  <Line type="monotone" dataKey="butceden_kayip_pay_pct" name="Bütçeden kayıp" stroke="#ef4444" dot={false} />
                  <Line type="monotone" dataKey="siralamadan_kayip_pay_pct" name="Sıralamadan kayıp" stroke="#f59e0b" dot={false} />
                  <Line type="monotone" dataKey="marka_kampanyasi_is_pct" name="Marka kampanyası" stroke="#6366f1" strokeWidth={2} dot={false} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Marka terimlerinde günlük CPC ve hacim" icon={Shield} hint="son 45 gün">
            <div className="max-h-[520px] overflow-y-auto">
              <Table
                columns={[
                  { key: 'tarih', label: 'Tarih' },
                  { key: 'gosterim', label: 'Gösterim', right: true, render: (r) => n0(r.gosterim) },
                  { key: 'tiklama', label: 'Tıklama', right: true, render: (r) => n0(r.tiklama) },
                  { key: 'harcama', label: 'Harcama', right: true, render: (r) => tl(r.harcama) },
                  { key: 'ortalama_cpc', label: 'Ort. CPC', right: true, render: (r) => tl2(r.ortalama_cpc) },
                  { key: 'ctr_pct', label: 'CTR', right: true, render: (r) => pct(r.ctr_pct) },
                  { key: 'ciro', label: 'Ciro (Google atıf)', right: true, render: (r) => tl(r.ciro) },
                  { key: 'kelime_sayisi', label: 'Kelime', right: true },
                ]}
                rows={sonGunler(data?.markaKelime)}
              />
            </div>
          </Card>
          <Card tone="warn">
            <div className="p-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Rakip bazlı auction insights (örtüşme oranı, dıştan sıralama, rakip gösterim payı) Google Ads
                API'de yok. Excel'in <strong>5c</strong> sayfası bu veri için boş şablon — arayüz export'u
                yapıştırıldığında tablo tamamlanır.
              </p>
            </div>
          </Card>
        </div>
      )}

      {u.uyarilar?.length > 0 && (
        <Card title="Üretim uyarıları" icon={Info}>
          <ul className="px-5 py-3 space-y-1 text-[13px] text-gray-600 list-disc list-inside">
            {u.uyarilar.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Card>
      )}

      <div className="flex items-center gap-2 text-[11px] text-gray-400 pb-6">
        <Download className="w-3.5 h-3.5" />
        Sayfadaki tablolar Excel'in özetidir; 26 bin satırlık GA4 kaynak/medium kırılımı ve kampanya×gün
        gösterim payı yalnızca Excel'de yer alır.
      </div>
    </div>
  );
}
